import { TrendingUp, TrendingDown, Users, Eye, Clock, MousePointerClick, RefreshCw, AlertCircle, Search, Target, BarChart3, Calendar, ChevronDown } from 'lucide-react';
import { cn, formatCompact } from '@/lib/utils';
import { useGA4KPIs, useGA4TrafficOverTime, useGA4TopPages, useGA4TrafficSources } from '@/hooks/useGA4Data';
import { useGSCKPIs, useGSCTopQueries, useGSCPerformanceOverTime } from '@/hooks/useGSCData';
import { useAuthStore } from '@/stores/auth-store';
import { useSettingsStore, type DateRangePreset } from '@/stores/settings-store';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { format } from 'date-fns';
import { useState } from 'react';

const dateRangeOptions: { value: DateRangePreset; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '14d', label: 'Last 14 days' },
  { value: '28d', label: 'Last 28 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '365d', label: 'Last 365 days' },
];

export function MainContent() {
  const { activePropertyId, properties } = useAuthStore();
  const { dateRange, setDateRange } = useSettingsStore();
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGA4 = !!activeProperty?.ga4Property;
  const hasGSC = !!activeProperty?.gscSite;
  const currentDateOption = dateRangeOptions.find((o) => o.value === dateRange) || dateRangeOptions[2];

  // GA4 hooks
  const { data: kpis, isLoading: kpisLoading, error: kpisError, refetch: refetchKpis } = useGA4KPIs();
  const { data: trafficData, isLoading: trafficLoading } = useGA4TrafficOverTime();
  const { data: topPages, isLoading: pagesLoading } = useGA4TopPages(5);
  const { data: trafficSources, isLoading: sourcesLoading } = useGA4TrafficSources(5);

  // GSC hooks
  const { data: gscKpis, isLoading: gscKpisLoading, refetch: refetchGscKpis } = useGSCKPIs();
  const { data: gscTopQueries, isLoading: gscQueriesLoading } = useGSCTopQueries(5);
  const { data: gscPerformance, isLoading: gscPerformanceLoading } = useGSCPerformanceOverTime();

  // Format duration from seconds to MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format date for chart
  const formatChartDate = (dateStr: string): string => {
    try {
      // GA4 returns dates as YYYYMMDD
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return format(new Date(`${year}-${month}-${day}`), 'MMM d');
    } catch {
      return dateStr;
    }
  };

  if (!hasGA4 && !hasGSC) {
    return (
      <main className="flex-1 overflow-auto bg-background p-6">
        <div className="flex h-full flex-col items-center justify-center text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">No Property Selected</h2>
          <p className="text-muted-foreground max-w-md">
            Select a Google Analytics 4 or Search Console property from the sidebar to view your dashboard data.
            If you don't see any properties, make sure you have access in your Google account.
          </p>
        </div>
      </main>
    );
  }

  // Combined refetch for both GA4 and GSC
  const handleRefresh = () => {
    if (hasGA4) refetchKpis();
    if (hasGSC) refetchGscKpis();
  };

  return (
    <main className="flex-1 overflow-auto bg-background p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            {activeProperty?.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Date Range Selector */}
          <div className="relative">
            <button
              onClick={() => setDateDropdownOpen(!dateDropdownOpen)}
              className="flex items-center gap-2 rounded-lg bg-surface px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors border border-border"
            >
              <Calendar className="h-4 w-4 text-muted-foreground" />
              {currentDateOption.label}
              <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', dateDropdownOpen && 'rotate-180')} />
            </button>
            {dateDropdownOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setDateDropdownOpen(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 w-44 rounded-lg border border-border bg-surface shadow-lg overflow-hidden">
                  {dateRangeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setDateRange(option.value);
                        setDateDropdownOpen(false);
                      }}
                      className={cn(
                        'w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors',
                        option.value === dateRange ? 'bg-primary/10 text-primary' : 'text-foreground'
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 rounded-lg bg-surface px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors border border-border"
          >
            <RefreshCw className={cn('h-4 w-4', (kpisLoading || gscKpisLoading) && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error State */}
      {kpisError && (
        <div className="mb-6 glass-panel p-4 border-l-4 border-danger">
          <p className="text-sm text-danger">
            Failed to load analytics data. Make sure you have access to this GA4 property.
          </p>
        </div>
      )}

      {/* GA4 KPI Cards */}
      {hasGA4 && (
        <div className="mb-6 grid grid-cols-4 gap-4">
          <KPICard
            label="Sessions"
            value={kpis?.sessions ?? 0}
            change={kpis?.sessionsChange ?? 0}
            icon={Users}
            isLoading={kpisLoading}
            variant="ga4"
          />
          <KPICard
            label="Users"
            value={kpis?.users ?? 0}
            change={kpis?.usersChange ?? 0}
            icon={Eye}
            isLoading={kpisLoading}
            variant="ga4"
          />
          <KPICard
            label="Avg. Duration"
            value={formatDuration(kpis?.avgSessionDuration ?? 0)}
            change={kpis?.avgSessionDurationChange ?? 0}
            icon={Clock}
            isLoading={kpisLoading}
            isFormatted
            variant="ga4"
          />
          <KPICard
            label="Bounce Rate"
            value={kpis?.bounceRate ?? 0}
            change={kpis?.bounceRateChange ?? 0}
            icon={MousePointerClick}
            isLoading={kpisLoading}
            isPercent
            invertTrend
            variant="ga4"
          />
        </div>
      )}

      {/* GSC KPI Cards */}
      {hasGSC && (
        <div className="mb-6">
          <div className="grid grid-cols-4 gap-4">
            <KPICard
              label="Clicks"
              value={gscKpis?.clicks ?? 0}
              change={gscKpis?.clicksChange ?? 0}
              icon={MousePointerClick}
              isLoading={gscKpisLoading}
              variant="gsc"
            />
            <KPICard
              label="Impressions"
              value={gscKpis?.impressions ?? 0}
              change={gscKpis?.impressionsChange ?? 0}
              icon={Eye}
              isLoading={gscKpisLoading}
              variant="gsc"
            />
            <KPICard
              label="CTR"
              value={gscKpis?.ctr ?? 0}
              change={gscKpis?.ctrChange ?? 0}
              icon={BarChart3}
              isLoading={gscKpisLoading}
              isPercent
              variant="gsc"
            />
            <KPICard
              label="Avg. Position"
              value={gscKpis?.position?.toFixed(1) ?? '-'}
              change={gscKpis?.positionChange ?? 0}
              icon={Target}
              isLoading={gscKpisLoading}
              isFormatted
              invertTrend
              variant="gsc"
            />
          </div>
        </div>
      )}

      {/* GA4 Charts */}
      {hasGA4 && (
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Traffic Chart */}
        <div className="glass-panel p-4">
          <h3 className="text-sm font-medium text-foreground mb-4">Traffic Over Time</h3>
          {trafficLoading ? (
            <div className="h-64 flex items-center justify-center">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : trafficData && trafficData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trafficData}>
                  <defs>
                    <linearGradient id="sessionsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(210, 100%, 60%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(210, 100%, 60%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatChartDate}
                    stroke="hsl(0, 0%, 45%)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="hsl(0, 0%, 45%)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => formatCompact(v)}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(222, 35%, 10%)',
                      border: '1px solid hsl(222, 30%, 20%)',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                    labelFormatter={(label) => formatChartDate(label as string)}
                  />
                  <Area
                    type="monotone"
                    dataKey="sessions"
                    stroke="hsl(210, 100%, 60%)"
                    fill="url(#sessionsGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No traffic data available
            </div>
          )}
        </div>

        {/* Top Pages */}
        <div className="glass-panel p-4">
          <h3 className="text-sm font-medium text-foreground mb-4">Top Pages</h3>
          {pagesLoading ? (
            <div className="h-64 flex items-center justify-center">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : topPages && topPages.length > 0 ? (
            <div className="space-y-3">
              {topPages.map((page: { pagePath: string; pageviews: number; bounceRate: number }, i: number) => (
                <div key={page.pagePath} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{page.pagePath}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatCompact(page.pageviews)} views
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-foreground">{page.bounceRate.toFixed(1)}%</p>
                    <p className="text-xs text-muted-foreground">bounce</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No page data available
            </div>
          )}
        </div>
      </div>
      )}

      {/* GA4 Traffic Sources */}
      {hasGA4 && (
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="glass-panel p-4 col-span-2">
          <h3 className="text-sm font-medium text-foreground mb-4">Traffic Sources</h3>
          {sourcesLoading ? (
            <div className="h-48 flex items-center justify-center">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : trafficSources && trafficSources.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trafficSources} layout="vertical">
                  <XAxis type="number" stroke="hsl(0, 0%, 45%)" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis
                    dataKey="source"
                    type="category"
                    stroke="hsl(0, 0%, 45%)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    width={80}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(222, 35%, 10%)',
                      border: '1px solid hsl(222, 30%, 20%)',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                  <Bar dataKey="sessions" fill="hsl(210, 100%, 60%)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-muted-foreground">
              No source data available
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="glass-panel p-4">
          <h3 className="text-sm font-medium text-foreground mb-4">Quick Stats</h3>
          <div className="space-y-4">
            <div>
              <p className="text-2xl font-semibold text-foreground">
                {kpis ? formatCompact(kpis.pageviews) : '-'}
              </p>
              <p className="text-sm text-muted-foreground">Total Pageviews</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground">
                {kpis ? (kpis.pageviews / (kpis.sessions || 1)).toFixed(1) : '-'}
              </p>
              <p className="text-sm text-muted-foreground">Pages / Session</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground">
                {trafficSources?.length ?? 0}
              </p>
              <p className="text-sm text-muted-foreground">Traffic Sources</p>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* GSC Section - Top Search Queries */}
      {hasGSC && (
        <div className="grid grid-cols-2 gap-4">
          {/* Top Search Queries */}
          <div className="glass-panel p-4">
            <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              Top Search Queries
            </h3>
            {gscQueriesLoading ? (
              <div className="h-64 flex items-center justify-center">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : gscTopQueries && gscTopQueries.length > 0 ? (
              <div className="space-y-3">
                {gscTopQueries.map((query, i) => (
                  <div key={query.query} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-4">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground truncate">{query.query}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatCompact(query.clicks)} clicks · {formatCompact(query.impressions)} impr
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-foreground">{query.ctr.toFixed(1)}%</p>
                      <p className="text-xs text-muted-foreground">CTR</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No search query data available
              </div>
            )}
          </div>

          {/* Search Performance Chart */}
          <div className="glass-panel p-4">
            <h3 className="text-sm font-medium text-foreground mb-4">Search Performance</h3>
            {gscPerformanceLoading ? (
              <div className="h-64 flex items-center justify-center">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : gscPerformance && gscPerformance.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={gscPerformance}>
                    <defs>
                      <linearGradient id="clicksGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(142, 76%, 45%)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(142, 76%, 45%)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => {
                        try {
                          return format(new Date(d), 'MMM d');
                        } catch {
                          return d;
                        }
                      }}
                      stroke="hsl(0, 0%, 45%)"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="hsl(0, 0%, 45%)"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => formatCompact(v)}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(222, 35%, 10%)',
                        border: '1px solid hsl(222, 30%, 20%)',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                      labelFormatter={(label) => {
                        try {
                          return format(new Date(label as string), 'MMM d, yyyy');
                        } catch {
                          return label;
                        }
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="clicks"
                      stroke="hsl(142, 76%, 45%)"
                      fill="url(#clicksGradient)"
                      strokeWidth={2}
                      name="Clicks"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No search performance data available
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
}

interface KPICardProps {
  label: string;
  value: number | string;
  change: number;
  icon: React.ComponentType<{ className?: string }>;
  isPercent?: boolean;
  invertTrend?: boolean;
  isLoading?: boolean;
  isFormatted?: boolean;
  variant?: 'default' | 'ga4' | 'gsc';
}

const variantStyles = {
  default: {
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    border: '',
  },
  ga4: {
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-400',
    border: 'border-l-2 border-blue-500/50',
  },
  gsc: {
    iconBg: 'bg-emerald-500/10',
    iconColor: 'text-emerald-400',
    border: 'border-l-2 border-emerald-500/50',
  },
};

function KPICard({ label, value, change, icon: Icon, isPercent, invertTrend, isLoading, isFormatted, variant = 'default' }: KPICardProps) {
  const isPositive = invertTrend ? change < 0 : change > 0;
  const TrendIcon = change >= 0 ? TrendingUp : TrendingDown;
  const styles = variantStyles[variant];

  const displayValue = isLoading
    ? '-'
    : isFormatted
      ? value
      : typeof value === 'number'
        ? isPercent
          ? `${value.toFixed(1)}%`
          : formatCompact(value)
        : value;

  return (
    <div className={cn('glass-panel p-4', styles.border)}>
      <div className="flex items-center justify-between">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg', styles.iconBg)}>
          <Icon className={cn('h-5 w-5', styles.iconColor)} />
        </div>
        {!isLoading && (
          <div
            className={cn(
              'flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
              isPositive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
            )}
          >
            <TrendIcon className="h-3 w-3" />
            {Math.abs(change).toFixed(1)}%
          </div>
        )}
      </div>
      <div className="mt-3">
        <p className={cn('text-2xl font-semibold text-foreground', isLoading && 'animate-pulse')}>
          {displayValue}
        </p>
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}
